import { useState, useEffect, useCallback, useMemo } from 'react';
import { adminAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { CheckCircle, XCircle, DollarSign, Eye, X } from 'lucide-react';
import { toast } from 'sonner';
import { AdminPageShell, SortIcon, createSortHelpers, EmptyRow } from '../components/AdminShared';

export default function AdminInvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [interpreterFilter, setInterpreterFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortCol, setSortCol] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [showDetail, setShowDetail] = useState(null);

  const { toggleSort } = createSortHelpers(sortCol, sortDir, setSortCol, setSortDir);

  const loadInvoices = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const res = await adminAPI.listInvoices(params);
      setInvoices(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  // Unique interpreters for filter dropdown
  const interpreters = useMemo(() => {
    const map = {};
    invoices.forEach(inv => { if (inv.interpreter_id) map[inv.interpreter_id] = inv.interpreter_name; });
    return Object.entries(map).sort((a, b) => (a[1] || '').localeCompare(b[1] || ''));
  }, [invoices]);

  // Filtered + sorted invoices
  const filtered = useMemo(() => {
    let list = invoices.filter(inv => {
      if (interpreterFilter && inv.interpreter_id !== interpreterFilter) return false;
      if (dateFrom && inv.created_at < dateFrom) return false;
      if (dateTo && inv.created_at > dateTo + 'T23:59:59') return false;
      return true;
    });

    return [...list].sort((a, b) => {
      let va, vb;
      switch (sortCol) {
        case 'number': va = a.invoice_number || ''; vb = b.invoice_number || ''; break;
        case 'interpreter': va = a.interpreter_name || ''; vb = b.interpreter_name || ''; break;
        case 'amount': va = a.amount_cents || 0; vb = b.amount_cents || 0; return sortDir === 'asc' ? va - vb : vb - va;
        case 'date': va = a.created_at || ''; vb = b.created_at || ''; break;
        case 'status': va = a.status || ''; vb = b.status || ''; break;
        default: va = a.created_at || ''; vb = b.created_at || '';
      }
      const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [invoices, interpreterFilter, dateFrom, dateTo, sortCol, sortDir]);

  const handleAction = async (id, action) => {
    try {
      if (action === 'approve') await adminAPI.approveInvoice(id);
      else if (action === 'reject') await adminAPI.rejectInvoice(id);
      else if (action === 'pay') await adminAPI.payInvoice(id);
      toast.success(`Invoice ${action}d`);
      loadInvoices();
      setShowDetail(null);
    } catch (e) { toast.error(e.response?.data?.detail || `Failed to ${action}`); }
  };

  const statusBadge = (s) => {
    const map = { submitted: 'default', approved: 'secondary', rejected: 'destructive', paid: 'outline' };
    const colorMap = { paid: 'bg-green-500/10 text-green-600 border-green-500/20' };
    return <Badge variant={map[s] || 'outline'} className={`text-xs capitalize ${colorMap[s] || ''}`}>{s}</Badge>;
  };

  const hasFilters = interpreterFilter || dateFrom || dateTo || statusFilter;
  const clearFilters = () => { setInterpreterFilter(''); setDateFrom(''); setDateTo(''); setStatusFilter(''); };

  return (
    <AdminPageShell
      loading={loading}
      title="Interpreter Invoices"
      subtitle="Review, approve, and process interpreter payment invoices"
      testId="admin-invoices-page"
    >
      {/* Filter Bar */}
      <div className="flex flex-wrap items-end gap-3 mb-5" data-testid="invoice-filters">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground font-medium">Status</label>
          <Select value={statusFilter || '_all'} onValueChange={v => setStatusFilter(v === '_all' ? '' : v)}>
            <SelectTrigger className="w-36 h-9" data-testid="invoice-status-filter"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All Statuses</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground font-medium">Interpreter</label>
          <Select value={interpreterFilter || '_all'} onValueChange={v => setInterpreterFilter(v === '_all' ? '' : v)}>
            <SelectTrigger className="w-44 h-9" data-testid="invoice-interpreter-filter"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All Interpreters</SelectItem>
              {interpreters.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground font-medium">From</label>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-36 h-9" data-testid="invoice-date-from" />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground font-medium">To</label>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-36 h-9" data-testid="invoice-date-to" />
        </div>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 text-xs text-muted-foreground" data-testid="clear-invoice-filters">
            <X className="w-3 h-3 mr-1" />Clear
          </Button>
        )}
        <div className="ml-auto text-xs text-muted-foreground self-end pb-2">{filtered.length} invoice{filtered.length !== 1 ? 's' : ''}</div>
      </div>

      {/* Table */}
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('number')}>Invoice #<SortIcon col="number" sortCol={sortCol} sortDir={sortDir} /></TableHead>
            <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('interpreter')}>Interpreter<SortIcon col="interpreter" sortCol={sortCol} sortDir={sortDir} /></TableHead>
            <TableHead>Description</TableHead>
            <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('amount')}>Amount<SortIcon col="amount" sortCol={sortCol} sortDir={sortDir} /></TableHead>
            <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('date')}>Date<SortIcon col="date" sortCol={sortCol} sortDir={sortDir} /></TableHead>
            <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('status')}>Status<SortIcon col="status" sortCol={sortCol} sortDir={sortDir} /></TableHead>
            <TableHead>Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map(inv => (
              <TableRow key={inv.id} data-testid={`admin-invoice-row-${inv.id}`}>
                <TableCell className="font-mono text-xs">{inv.invoice_number}</TableCell>
                <TableCell className="text-sm">{inv.interpreter_name}</TableCell>
                <TableCell className="text-sm max-w-[200px] truncate">{inv.description}</TableCell>
                <TableCell className="font-mono font-semibold">${(inv.amount_cents / 100).toFixed(2)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{new Date(inv.created_at).toLocaleDateString()}</TableCell>
                <TableCell>{statusBadge(inv.status)}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShowDetail(inv)} data-testid={`view-invoice-${inv.id}`}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    {inv.status === 'submitted' && (
                      <Button variant="ghost" size="icon" className="text-green-600 h-8 w-8" onClick={() => handleAction(inv.id, 'approve')} data-testid={`approve-invoice-${inv.id}`}>
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                    )}
                    {inv.status === 'submitted' && (
                      <Button variant="ghost" size="icon" className="text-destructive h-8 w-8" onClick={() => handleAction(inv.id, 'reject')} data-testid={`reject-invoice-${inv.id}`}>
                        <XCircle className="w-4 h-4" />
                      </Button>
                    )}
                    {inv.status === 'approved' && (
                      <Button variant="ghost" size="icon" className="text-green-600 h-8 w-8" onClick={() => handleAction(inv.id, 'pay')} data-testid={`pay-invoice-${inv.id}`}>
                        <DollarSign className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <EmptyRow colSpan={7} message="No invoices found" />}
          </TableBody>
        </Table>
      </div>

      {/* Invoice Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent data-testid="invoice-detail-dialog">
          <DialogHeader><DialogTitle>Invoice Details</DialogTitle></DialogHeader>
          {showDetail && (
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div><span className="text-muted-foreground">Invoice #:</span> <span className="font-mono">{showDetail.invoice_number}</span></div>
                <div><span className="text-muted-foreground">Interpreter:</span> {showDetail.interpreter_name}</div>
                <div><span className="text-muted-foreground">Amount:</span> <span className="font-mono font-bold">${(showDetail.amount_cents / 100).toFixed(2)}</span></div>
                <div><span className="text-muted-foreground">Status:</span> {statusBadge(showDetail.status)}</div>
                <div className="col-span-2"><span className="text-muted-foreground">Description:</span> {showDetail.description}</div>
                {showDetail.notes && <div className="col-span-2"><span className="text-muted-foreground">Notes:</span> {showDetail.notes}</div>}
                <div><span className="text-muted-foreground">Created:</span> {new Date(showDetail.created_at).toLocaleString()}</div>
                {showDetail.paid_at && <div><span className="text-muted-foreground">Paid:</span> {new Date(showDetail.paid_at).toLocaleString()}</div>}
              </div>
            </div>
          )}
          <DialogFooter>
            {showDetail?.status === 'submitted' && (
              <div className="flex gap-2">
                <Button variant="outline" className="text-destructive" onClick={() => handleAction(showDetail.id, 'reject')}>Reject</Button>
                <Button className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" onClick={() => handleAction(showDetail.id, 'approve')}>Approve</Button>
              </div>
            )}
            {showDetail?.status === 'approved' && (
              <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={() => handleAction(showDetail.id, 'pay')}>Mark as Paid</Button>
            )}
            <Button variant="outline" onClick={() => setShowDetail(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPageShell>
  );
}
